// The status pill's countdown (the classic boards' ring and its number) read at
// any moment, and the frame loop that draws it.
//
// The pill used to read its count only when it rendered, and it rendered on
// `useTimer`'s 1 s tick, started whenever the page mounted: the ring moved by
// a second's worth at a time through a 0.5 s CSS transition and then stood
// still, the number turned up to a second after its second, a hard round's
// lock showed when `race_starting` happened to land, and every restart (the
// lock's count of X, a new round) swept the ring backwards. Now the ring's
// fill is a continuous function of the clocks (`getRaceCountdownSpan`), drawn
// every animation frame while a count runs, and the pill re-renders on the
// frame its number (or a hard round's stage) changes.
import {
  getRaceCountdownInfo,
  getRaceCountdownSpan,
  spanProgressAt,
  type CountdownSpan,
  type RaceCountdownInfo
} from 'src/components/CrashChipPanel/raceCountdown'
import type { Race } from 'src/types/domain/Race'
import {
  hardCloseClock,
  hardCountdown,
  type HardCloseClock,
  type HardCountdown
} from 'src/utils/hardCloseClock'

/** The ring's circumference, px (`r="18"`; the stylesheet's `stroke-dasharray`). */
export const RING_CIRCUMFERENCE_PX = 113

/** The circle's `stroke-dashoffset` for a fill of 0..100. */
export const ringDashOffset = (fill: number): string =>
  `${RING_CIRCUMFERENCE_PX - (Math.min(Math.max(fill, 0), 100) / 100) * RING_CIRCUMFERENCE_PX}px`

export interface StatusCountdownInput {
  race: Pick<Race, 'uuid' | 'raceEvents'>
  previousRace: Pick<Race, 'raceEvents'> | undefined
  /** `getBettingTimeWindow(game)`: a legacy round's count, seconds. */
  bettingWindowSec: number
  /** `getSettledTimeWindow(game)`. */
  settledWindowSec: number
  /** The pill's own between-rounds count (its 1 Hz interval), or null. */
  finalCountdown: number | null
  betweenRoundsAnchorsOnEnded: boolean
}

export interface StatusCountdown {
  hardClock: HardCloseClock | null
  hardCount: HardCountdown | null
  info: RaceCountdownInfo
  /** The span the ring runs over now; null: the ring stands still at `ring`. */
  span: CountdownSpan | null
  /** The ring's fill now, 0..100. */
  ring: number
  /** What moves with time on the pill besides the ring: a new key needs a render. */
  key: string
}

/** The pill's countdown at `perf` (`performance.now()`) and `wall` (`Date.now()`). */
export function statusCountdownAt(
  input: StatusCountdownInput,
  at: { perf: number; wall: number }
): StatusCountdown {
  const hardClock = hardCloseClock(input.race, input.previousRace, at.perf)
  const hardCount = hardClock ? hardCountdown(hardClock, at.perf) : null
  const inputs = {
    hard: hardCount,
    hardWindow: hardClock ? hardClock.close : null,
    raceEvents: input.race.raceEvents,
    nowMs: at.wall,
    bettingWindowSec: input.bettingWindowSec,
    settledWindowSec: input.settledWindowSec,
    finalCountdown: input.finalCountdown,
    betweenRoundsAnchorsOnEnded: input.betweenRoundsAnchorsOnEnded
  }
  const info = getRaceCountdownInfo(inputs)
  const span = getRaceCountdownSpan(inputs)
  const ring = span
    ? spanProgressAt(span, span.clock === 'perf' ? at.perf : at.wall)
    : Math.min(Math.max(info.progress, 0), 100)
  return {
    hardClock,
    hardCount,
    info,
    span,
    ring,
    key: `${hardCount ? hardCount.stage : ''}|${info.current}`
  }
}

const requestFrameDefault = (callback: () => void): number =>
  typeof requestAnimationFrame === 'function'
    ? requestAnimationFrame(callback)
    : (setTimeout(callback, 16) as unknown as number)

const cancelFrameDefault = (id: number): void => {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id)
  else clearTimeout(id as unknown as ReturnType<typeof setTimeout>)
}

/**
 * Draw the ring every frame from `read`, and ask for a render on the frame the
 * key changes. Draws once at once; returns the stop.
 */
export function runRingFrames(input: {
  read: () => { ring: number; key: string }
  /** The key of the render that started the loop. */
  initialKey: string
  draw: (ring: number) => void
  render: () => void
  requestFrame?: (callback: () => void) => number
  cancelFrame?: (id: number) => void
}): () => void {
  const requestFrame = input.requestFrame ?? requestFrameDefault
  const cancelFrame = input.cancelFrame ?? cancelFrameDefault
  let drawn: number | undefined
  let key = input.initialKey
  let id: number | null = null
  let stopped = false

  const frame = () => {
    id = null
    if (stopped) return
    const now = input.read()
    if (now.ring !== drawn) {
      drawn = now.ring
      input.draw(now.ring)
    }
    if (now.key !== key) {
      key = now.key
      input.render()
    }
    id = requestFrame(frame)
  }
  frame()

  return () => {
    stopped = true
    if (id !== null) cancelFrame(id)
  }
}
